import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUserExpenseSheets, useCreateExpenseSheet, useDeleteExpenseSheet, useSubmitExpenseSheet, useCancelExpenseSheet } from '../../hooks/useExpenses';
import { StatusBadge } from '../../components/workflow/StatusBadge';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import ExpenseForm from '../../components/domain/ExpenseForm';
import type { CreateExpenseSheetInput } from '../../services/api/expenses';

const STATES = ['all', 'open', 'submitted', 'approved', 'rejected', 'cancelled'];

export default function ExpenseListPage() {
  const navigate = useNavigate();
  const [stateFilter, setStateFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [confirmSubmitId, setConfirmSubmitId] = useState<number | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  // F1-006: surface mutation rejections inline instead of leaking as
  // unhandled promise rejections (which would also leave the confirm
  // modals stuck open). Mirror of F24-002 / F25-002 fix pattern.
  const [actionError, setActionError] = useState<string | null>(null);

  const { data: sheets, isLoading, error } = useUserExpenseSheets({
    state: stateFilter !== 'all' ? stateFilter : undefined,
  });
  const createMutation = useCreateExpenseSheet();
  const deleteMutation = useDeleteExpenseSheet();
  const submitMutation = useSubmitExpenseSheet();
  const cancelMutation = useCancelExpenseSheet();

  const handleCreate = async (input: CreateExpenseSheetInput) => {
    setActionError(null);
    try {
      const created = await createMutation.mutateAsync(input);
      setShowForm(false);
      // Redirect to detail page so user can add receipts
      if (created?.id) {
        navigate(`/expenses/${created.id}`);
      }
    } catch (err) {
      setActionError((err as Error).message || 'Failed to create expense sheet');
    }
  };

  const handleSubmit = async () => {
    if (confirmSubmitId === null) return;
    setActionError(null);
    try {
      await submitMutation.mutateAsync(confirmSubmitId);
    } catch (err) {
      setActionError((err as Error).message || 'Failed to submit expense sheet');
    } finally {
      setConfirmSubmitId(null);
    }
  };

  const handleCancel = async () => {
    if (confirmCancelId === null) return;
    setActionError(null);
    try {
      await cancelMutation.mutateAsync(confirmCancelId);
    } catch (err) {
      setActionError((err as Error).message || 'Failed to cancel expense sheet');
    } finally {
      setConfirmCancelId(null);
    }
  };

  const handleDelete = async () => {
    if (confirmDeleteId === null) return;
    setActionError(null);
    try {
      await deleteMutation.mutateAsync(confirmDeleteId);
    } catch (err) {
      setActionError((err as Error).message || 'Failed to delete expense sheet');
    } finally {
      setConfirmDeleteId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load expense sheets: {(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Expenses</h1>
        <Button onClick={() => setShowForm(true)}>New Expense</Button>
      </div>

      <DismissibleErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {STATES.map((state) => (
          <button
            key={state}
            onClick={() => setStateFilter(state)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              stateFilter === state
                ? 'bg-primary-100 text-primary-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {state === 'all' ? 'All' : state.charAt(0).toUpperCase() + state.slice(1)}
          </button>
        ))}
      </div>

      {/* Create Form */}
      {showForm && (
        <ExpenseForm
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          isLoading={createMutation.isPending}
        />
      )}

      {/* List */}
      {!sheets || sheets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No expense sheets found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sheets.map((sheet) => (
            <div
              key={sheet.id}
              className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between"
            >
              <Link to={`/expenses/${sheet.id}`} className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900 truncate">
                    {sheet.description || 'Expense Sheet'}
                  </span>
                  <StatusBadge state={sheet.state} size="sm" />
                  {sheet.isReimbursed && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Reimbursed
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {sheet.project?.name && <span className="mr-2">{sheet.project.name}</span>}
                  <span className="text-gray-400">
                    {sheet.receiptCount} receipt{sheet.receiptCount !== 1 ? 's' : ''} &middot; {sheet.totalAmount.toFixed(2)} CZK
                  </span>
                </p>
              </Link>
              <div className="flex items-center gap-2 ml-4">
                {sheet.state === 'open' && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => setConfirmSubmitId(sheet.id)}
                      disabled={sheet.receiptCount === 0}
                      title={sheet.receiptCount === 0 ? 'Add receipts before submitting' : undefined}
                    >
                      Submit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(sheet.id)} className="text-red-600">
                      Delete
                    </Button>
                  </>
                )}
                {sheet.state === 'cancelled' && (
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(sheet.id)} className="text-red-600">
                    Delete
                  </Button>
                )}
                {sheet.state === 'submitted' && (
                  <Button variant="ghost" size="sm" onClick={() => setConfirmCancelId(sheet.id)} className="text-red-600">
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Submit Confirmation */}
      <ConfirmModal
        isOpen={confirmSubmitId !== null}
        onClose={() => setConfirmSubmitId(null)}
        onConfirm={handleSubmit}
        title="Submit Expense Sheet"
        message="Are you sure you want to submit this expense sheet for approval?"
        confirmText="Submit"
        isLoading={submitMutation.isPending}
      />

      {/* Cancel Confirmation */}
      <ConfirmModal
        isOpen={confirmCancelId !== null}
        onClose={() => setConfirmCancelId(null)}
        onConfirm={handleCancel}
        title="Cancel Expense Sheet"
        message="Are you sure you want to cancel this expense sheet?"
        confirmText="Cancel Sheet"
        variant="danger"
        isLoading={cancelMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Expense Sheet"
        message="Are you sure you want to delete this expense sheet? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
